export interface SsrRequestContext {
  pageId: string;
  page: string;
  tokenLogged: boolean;
}

const CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';
function shortId(): string {
  return Array.from({ length: 6 }, () => CHARS[Math.floor(Math.random() * CHARS.length)]).join('');
}

// AsyncLocalStorage is a Node.js built-in — only available server-side.
// Dynamic require keeps this module safe to import from the client module graph
// (the require call is never executed in the browser bundle).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let storage: any = null;
function getStorage() {
  if (!storage && typeof window === 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { AsyncLocalStorage } = require('async_hooks');
    storage = new AsyncLocalStorage();
  }
  return storage;
}

/**
 * Run `fn` inside a new SSR request context.
 * All calls to `getSsrContext()` within the async subtree will see the same context.
 */
export function runWithSsrContext<T>(page: string, fn: () => T): T {
  const s = getStorage();
  if (!s) return fn();
  const ctx: SsrRequestContext = { pageId: shortId(), page, tokenLogged: false };
  return s.run(ctx, fn);
}

/** Returns the active context, or null if called outside a runWithSsrContext scope. */
export function getSsrContext(): SsrRequestContext | null {
  return getStorage()?.getStore() ?? null;
}

export type SsrPhase = 'meta' | 'ssr';

/**
 * Call at the very top of generateMetadata / Page component to establish a
 * named context for the entire render subtree.
 * If a context already exists (e.g. from a react.cache() shared fetch), this
 * is a no-op — the existing context and ID are reused.
 */
export function initSsrContext<T>(page: string, phase: SsrPhase, fn: () => T): T {
  const s = getStorage();
  if (!s) return fn();
  if (s.getStore()) return fn(); // already inside a context — reuse it
  const ctx: SsrRequestContext = {
    pageId: shortId(),
    page: `${page}:${phase}`,
    tokenLogged: false,
  };
  return s.run(ctx, fn);
}

/**
 * Use in async RSC children that are rendered under <Suspense> and therefore
 * run in a separate async tree, losing the parent's AsyncLocalStorage context.
 * Pass the pageId + page from the parent so all siblings share the same log ID.
 * No-op if a context already exists (i.e. the component is server-rendered
 * inline without a Suspense boundary).
 */
export function inheritSsrContext<T>(pageId: string, page: string, fn: () => T): T {
  const s = getStorage();
  if (!s) return fn();
  if (s.getStore()) return fn(); // already has context
  const ctx: SsrRequestContext = { pageId, page, tokenLogged: false };
  return s.run(ctx, fn);
}
